import { useEffect, useMemo } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import type { WorkflowEdge, WorkflowNode } from "@/components/Workflows/types";
import { useAuth } from "@/context/auth-context";
import {
  useWorkflowDetails,
  useWorkflowNodes,
  useWorkflowVersionDetail,
  useWorkflowVersions,
} from "@/queries";

interface WorkflowEntityPreviewProps {
  workspaceId: string;
  entityId: string;
  versionNumber?: string;
  onReadyChange: (isReady: boolean) => void;
}

const NODE_LIMIT = 8;
const EDGE_LIMIT = 6;

const getNodeTypeLabel = (nodeType: WorkflowNode["node_type"]) =>
  String(nodeType).replace(/_/g, " ").toLowerCase();

const getNodeLevel = (
  node: WorkflowNode,
  nodesById: Map<string, WorkflowNode>,
  seen = new Set<string>(),
): number => {
  if (seen.has(node.id) || node.dependencies.length === 0) {
    return 0;
  }

  seen.add(node.id);
  const dependencyLevels = node.dependencies.map(dependencyId => {
    const dependency = nodesById.get(dependencyId);
    return dependency ? getNodeLevel(dependency, nodesById, new Set(seen)) + 1 : 0;
  });

  return Math.max(0, ...dependencyLevels);
};

const getPreviewNodes = (nodes: WorkflowNode[]) => {
  const nodesById = new Map(nodes.map(node => [node.id, node]));
  return nodes
    .map(node => ({
      node,
      level: getNodeLevel(node, nodesById),
    }))
    .sort((a, b) => a.level - b.level || a.node.name.localeCompare(b.node.name))
    .slice(0, NODE_LIMIT);
};

const WorkflowNodeCard = ({ node, level }: { node: WorkflowNode; level: number }) => (
  <div
    className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
    style={{ marginLeft: Math.min(level, 3) * 28 }}
  >
    <div className="mb-3 flex items-start justify-between gap-3">
      <div>
        <p className="line-clamp-1 text-sm font-semibold text-slate-900">{node.name}</p>
        <p className="mt-1 text-xs font-medium capitalize text-slate-500">
          {getNodeTypeLabel(node.node_type)}
        </p>
      </div>
      {node.is_output_node && (
        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
          Output
        </span>
      )}
    </div>
    {node.dependencies.length > 0 && (
      <p className="line-clamp-1 text-xs text-slate-500">
        Depends on {node.dependencies.length} node{node.dependencies.length === 1 ? "" : "s"}
      </p>
    )}
  </div>
);

const WorkflowEdgeList = ({ edges }: { edges: WorkflowEdge[] }) => {
  if (!edges.length) {
    return <p className="text-sm text-slate-500">No connections found.</p>;
  }

  return (
    <div className="space-y-2">
      {edges.slice(0, EDGE_LIMIT).map(edge => (
        <div
          key={edge.id}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600"
        >
          <span className="font-medium text-slate-800">{edge.source_node_name}</span>
          <span className="mx-2 text-slate-400">→</span>
          <span className="font-medium text-slate-800">{edge.target_node_name}</span>
          {edge.conditionals.length > 0 && (
            <span className="ml-2 text-slate-400">· {edge.conditionals.length} condition(s)</span>
          )}
        </div>
      ))}
      {edges.length > EDGE_LIMIT && (
        <p className="text-xs font-medium text-slate-400">
          +{edges.length - EDGE_LIMIT} more connections
        </p>
      )}
    </div>
  );
};

export const WorkflowEntityPreview = ({
  workspaceId,
  entityId,
  versionNumber,
  onReadyChange,
}: WorkflowEntityPreviewProps) => {
  const userToken = useAuth()?.userToken ?? "";
  const requestedVersionNumber = versionNumber ? Number(versionNumber) : undefined;

  const workflowDetailsQuery = useWorkflowDetails(entityId, userToken);
  const workflowVersionsQuery = useWorkflowVersions(entityId, userToken);

  const workflowVersion = useMemo(() => {
    const versions = workflowVersionsQuery.data?.workflow_versions ?? [];
    if (requestedVersionNumber === undefined) {
      return versions[0];
    }

    return versions.find(version => version.number === requestedVersionNumber);
  }, [requestedVersionNumber, workflowVersionsQuery.data?.workflow_versions]);

  const workflowVersionId = workflowVersion?.id ?? 0;
  const workflowVersionDetailQuery = useWorkflowVersionDetail(
    workflowVersionId,
    Number(workspaceId),
    userToken,
  );
  const workflowNodesQuery = useWorkflowNodes(workflowVersionId, userToken);

  const workflow = workflowDetailsQuery.data?.workflow;
  const nodes = useMemo(
    () => workflowNodesQuery.data?.workflow_nodes ?? [],
    [workflowNodesQuery.data?.workflow_nodes],
  );
  const edges = useMemo(
    () => workflowVersionDetailQuery.data?.edges ?? [],
    [workflowVersionDetailQuery.data?.edges],
  );
  const requiredInputs = workflowVersionDetailQuery.data?.required_input_variables ?? {};
  const previewNodes = useMemo(() => getPreviewNodes(nodes), [nodes]);

  const isLoading =
    workflowDetailsQuery.isLoading ||
    workflowVersionsQuery.isLoading ||
    (Boolean(workflowVersionId) &&
      (workflowVersionDetailQuery.isLoading || workflowNodesQuery.isLoading));
  const hasError =
    workflowDetailsQuery.isError ||
    workflowVersionsQuery.isError ||
    workflowVersionDetailQuery.isError ||
    workflowNodesQuery.isError;
  const isReady = !isLoading && (hasError || Boolean(workflow && workflowVersion));

  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm">
        <LoadingSpinner />
      </div>
    );
  }

  if (hasError || !workflow || !workflowVersion) {
    return (
      <div className="h-full w-full rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
        <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-medium tracking-wide text-rose-700">
          WORKFLOW PREVIEW UNAVAILABLE
        </span>
        <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900">
          Could not load this workflow.
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-6 text-slate-600">
          The preview renderer could not fetch workflow details or version data for workflow{" "}
          {entityId}.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-white px-10 py-8">
        <div className="mb-5 flex items-center justify-between gap-4">
          <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-medium tracking-wide text-violet-700">
            WORKFLOW
          </span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">
            {requestedVersionNumber
              ? `v${workflowVersion.number}`
              : `Latest · v${workflowVersion.number}`}
          </span>
        </div>
        <h1 className="line-clamp-2 text-4xl font-semibold tracking-tight text-slate-950">
          {workflow.name}
        </h1>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            {nodes.length} node{nodes.length === 1 ? "" : "s"}
          </span>
          <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            {edges.length} connection{edges.length === 1 ? "" : "s"}
          </span>
          {Object.keys(requiredInputs).length > 0 && (
            <span className="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
              {Object.keys(requiredInputs).length} input variable
              {Object.keys(requiredInputs).length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 bg-slate-50/60 px-10 py-8">
        <div className="grid h-full grid-cols-[1fr_320px] gap-6">
          <div className="min-h-0 overflow-hidden rounded-2xl border border-slate-200 bg-slate-100/70 p-5">
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Workflow graph
              </p>
              {nodes.length > NODE_LIMIT && (
                <p className="text-xs font-medium text-slate-400">
                  Showing {NODE_LIMIT} of {nodes.length}
                </p>
              )}
            </div>
            <div className="space-y-3">
              {previewNodes.length > 0 ? (
                previewNodes.map(({ node, level }) => (
                  <WorkflowNodeCard key={node.id} node={node} level={level} />
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
                  This workflow version has no nodes yet.
                </div>
              )}
            </div>
          </div>

          <aside className="min-h-0 space-y-5 overflow-hidden rounded-2xl border border-slate-200 bg-white p-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Connections
              </p>
              <div className="mt-3">
                <WorkflowEdgeList edges={edges} />
              </div>
            </div>
            {workflowVersion.commit_message && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Version note
                </p>
                <p className="mt-2 line-clamp-4 text-sm leading-6 text-slate-700">
                  {workflowVersion.commit_message}
                </p>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};
